// Answer-first summaries, FAQs, search titles and related articles for service pages.
// Every statement is drawn from existing copy on the site (see src/data/services.ts);
// FAQPage JSON-LD is generated from the same arrays so visible and structured data match.
export type Faq = { question: string; answer: string };
export type ServiceSeo = {
  seoTitle: string;
  seoDescription: string;
  answerFirst?: { question: string; answer: string };
  faqs?: Faq[];
  relatedArticles: string[];
};

export const serviceSeo: Record<string, ServiceSeo> = {
  "textile-recycling": {
    seoTitle: "",
    seoDescription: "",
    relatedArticles: [
      "textile-and-uniform-recycling-services-for-corporate-waste-reduction-uk",
      "eco-friendly-ppe-disposal-for-businesses",
      "textile-waste-recycling-make-a-big-impact-today",
    ],
  },
  "confidential-waste-disposal": {
    seoTitle: "Confidential Waste Disposal for UK Business | Preserve Earth",
    seoDescription:
      "Confidential waste disposal and document shredding for UK organisations. Lockable consoles, scheduled or one-off collections and proof of destruction.",
    answerFirst: {
      question: "What is confidential waste disposal from Preserve Earth?",
      answer: "Confidential waste disposal from Preserve Earth is secure document shredding for businesses and public bodies across the UK that handle personal, financial, legal or other confidential data. We supply lockable consoles, collect them on a schedule that suits you or as a one-off, securely destroy the contents and issue an electronic note of secure destruction the working day after collection."
    },
    faqs: [
      {
        question: "How much does confidential waste shredding cost?",
        answer: "Every quote is tailored. The price depends on whether you need a single collection or a regular service, how much confidential waste you produce, the size and number of consoles, how often we collect and the length of your agreement. Share those details through the enquiry form and we will come back with a tailored quote."
      },
      {
        question: "Is a single collection priced differently from a regular service?",
        answer: "Yes. A one-off collection is quoted as a single job with no contract required. A regular service is quoted on the number and size of consoles on your site and how often we collect them."
      },
      {
        question: "How does the volume of waste affect my quote?",
        answer: "Volume is one of the main factors in your quote, so an estimate helps us price accurately. Approximate numbers of boxes, sacks or filing cabinets to clear are enough to start with."
      },
      {
        question: "Which console or container size do we need?",
        answer: "It depends on how much confidential paper each area of your site produces and how much space you have. We recommend the right console size and number as part of your quote."
      },
      {
        question: "How often will you collect?",
        answer: "We agree a collection frequency that suits your volume. Consoles are QR-code tracked and collected when full, and one-off collections can be booked whenever you need them."
      },
      {
        question: "Is there a minimum contract term?",
        answer: "One-off collections need no contract. For a regular service, the agreement length is set out in your tailored quote, so you know the term before you sign."
      },
      {
        question: "What proof of destruction will I receive?",
        answer: "An electronic note of secure destruction is issued the working day after collection. It details the quantity and type of waste collected, and confirmation it has been securely destroyed."
      },
      {
        question: "Do you collect across the UK?",
        answer: "Yes. We arrange collections across the UK. We also serve clients in France and Belgium, who courier their items securely to our depot in France. Collections are offered in the UK only."
      }
    ],
    relatedArticles: [
      "ico-confidential-waste-disposal-guide",
      "how-to-dispose-of-branded-stock-and-unsold-inventory-securely-and-sustainably",
      "eco-friendly-ppe-disposal-for-businesses",
    ],
  },
  "it-and-media-recycling": {
    seoTitle: "IT & Media Recycling and Data Destruction | Preserve Earth",
    seoDescription:
      "IT and media recycling for organisations across the UK: secure collection, destruction of hard drives and digital media, WEEE recycling and a full audit trail.",
    answerFirst: {
      question: "What is IT and media recycling from Preserve Earth?",
      answer: "IT and media recycling from Preserve Earth is secure collection and destruction for organisations across the UK retiring laptops, PCs, servers, hard drives and other digital media. We collect on a scheduled, ad-hoc or one-off basis, make data completely unrecoverable and recycle equipment in line with WEEE regulations, with a full audit trail issued."
    },
    faqs: [
      {
        question: "How much does IT recycling and data destruction cost?",
        answer: "Every quote is tailored. The price depends on whether you need a single clearance or regular collections, the types and number of items, how they are packed, how often we collect and the length of your agreement. Share those details through the enquiry form and we will come back with a tailored quote."
      },
      {
        question: "Is a one-off clearance priced differently from scheduled collections?",
        answer: "Yes. A one-off clearance after an upgrade or office move is quoted as a single job. Scheduled collections are quoted on your typical volumes and how often you need us."
      },
      {
        question: "How does volume affect my quote?",
        answer: "Volume and the mix of equipment are the main factors. A rough list, for example how many laptops, PCs, servers and loose hard drives, is enough for us to price accurately."
      },
      {
        question: "Do you supply containers for IT equipment?",
        answer: "It depends on what you are disposing of and how much. Tell us the types and approximate numbers of items and we will recommend how they should be packed and collected."
      },
      {
        question: "How often can you collect?",
        answer: "We offer frequently scheduled WEEE collections, ad-hoc collections when you need them and one-off electronic waste removal."
      },
      {
        question: "Is there a minimum contract term?",
        answer: "One-off and ad-hoc collections need no contract. For scheduled collections, the agreement length is set out in your tailored quote, so you know the term before you sign."
      },
      {
        question: "Does this service destroy data?",
        answer: "Yes. Hard drive destruction makes data completely unrecoverable, frees space in your office, supports regulatory compliance and reduces the risk of fines and penalties."
      },
      {
        question: "How do you handle WEEE?",
        answer: "We arrange collection and disposal of business IT and electrical equipment in line with the WEEE Regulations, and you receive documentation for your records."
      }
    ],
    relatedArticles: [
      "ico-confidential-waste-disposal-guide",
      "how-to-dispose-of-branded-stock-and-unsold-inventory-securely-and-sustainably",
    ],
  },
  "product-destruction": {
    seoTitle: "Secure Product Destruction for Brands UK | Preserve Earth",
    seoDescription:
      "Secure UK product destruction for faulty stock, excess inventory, counterfeit goods and branded items, with an electronic note of secure destruction.",
    answerFirst: {
      question: "What is secure product destruction from Preserve Earth?",
      answer:
        "Secure product destruction from Preserve Earth is for businesses that need unwanted, outdated, faulty or counterfeit goods removed from circulation. Items are inspected, hazardous parts are decommissioned, and products are securely destroyed and, where possible, responsibly recycled, with an electronic note of secure destruction issued to protect your brand.",
    },
    faqs: [
      {
        question: "Why do businesses use product destruction?",
        answer:
          "Product destruction protects your brand, prevents fraud and identity theft, and stops sensitive items being diverted to international markets where they could be resold or misused.",
      },
      {
        question: "What types of products can be destroyed?",
        answer:
          "Faulty products unsuitable for sale, excess stock (dead stock), illegal or counterfeit goods, and products containing confidential information such as ID cards, security badges and digital media.",
      },
      {
        question: "How does the product destruction process work?",
        answer:
          "Inspection to confirm items are safe for disposal and identify hazardous components. Decommissioning of hazardous parts (batteries, chemicals, electrical components) handled safely for people and the environment. Secure destruction, confirmed by an electronic note of secure destruction, and where possible responsible recycling of the residual material.",
      },
      {
        question: "Why should counterfeit goods be destroyed?",
        answer:
          "Counterfeit clothing, sunglasses, textiles and pirated media must be removed from circulation to protect consumers and prevent your brand being associated with illicit goods.",
      },
      {
        question: "How does destruction help with excess stock?",
        answer:
          "Professional destruction helps you manage excess inventory without risking discounted resale or items falling into the wrong hands.",
      },
    ],
    relatedArticles: [
      "how-to-dispose-of-branded-stock-and-unsold-inventory-securely-and-sustainably",
      "eco-friendly-ppe-disposal-for-businesses",
      "corporate-uniform-recycling-a-sustainable-solution-for-business-waste",
    ],
  },
  "one-off-shredding": {
    seoTitle: "One-off Shredding Service for Businesses | Preserve Earth",
    seoDescription:
      "One-off shredding for UK businesses: documents, textiles, workwear and IT equipment collected, securely destroyed and recycled. No contract required.",
    answerFirst: {
      question: "What is one-off shredding from Preserve Earth?",
      answer:
        "One-off shredding from Preserve Earth is a single-collection service for businesses that need to clear documents, textiles, workwear, IT equipment or unusable product without a regular contract. Items are securely collected, destroyed and, where possible, recycled, with an electronic note of secure destruction issued. It suits office moves, archive clear-outs and end-of-year clear outs.",
    },
    faqs: [
      {
        question: "Do I need a contract for one-off shredding?",
        answer:
          "No contract is required. Regular shredding contracts aren't always needed; many businesses just need a one-time service to clear space, improve sustainability or stay compliant.",
      },
      {
        question: "What can be included in a one-off collection?",
        answer:
          "Confidential papers and documents, textiles, workwear and uniforms, IT and electrical waste (WEEE), and product that is no longer usable or saleable.",
      },
      {
        question: "Can IT equipment be collected in a single visit and shredded off-site?",
        answer:
          "After an IT upgrade, leftover hard drives, PCs and servers are collected in a single visit, transported securely and shredded off-site at a secure facility, with an electronic note of secure destruction issued.",
      },
      {
        question: "Why destroy old workwear instead of throwing it out?",
        answer:
          "Throwing it out risks impersonation or brand damage. One-off workwear destruction keeps your brand, employees and reputation safe.",
      },
      {
        question: "Is one-off shredding available nationally?",
        answer: "Yes. The one-off shredding service offers national coverage.",
      },
    ],
    relatedArticles: [
      "eco-friendly-ppe-disposal-for-businesses",
      "corporate-uniform-recycling-a-sustainable-solution-for-business-waste",
      "how-to-dispose-of-branded-stock-and-unsold-inventory-securely-and-sustainably",
    ],
  },
};
